import React from "react";
import clsx from "clsx";
import { Repeat } from "lucide-react";

/**
 * Small picker for recurrence on the parent task. Maps three friendly presets
 * to compact RRULE-style strings consumed by the backend recurrence engine:
 *
 *   none           → null
 *   daily          → FREQ=DAILY;COUNT=N
 *   weekdays       → FREQ=WEEKDAYS;COUNT=N      (Mon-Fri only)
 *   weekly         → FREQ=WEEKLY;COUNT=N         (same day-of-week)
 *
 * Backend only materialises on demand — choosing a preset here is a hint, not
 * an action. The actual occurrences are spawned when the user clicks
 * "Materialise" in the task drawer (or on first scheduled block, if we ever
 * automate it).
 */
const PRESETS = [
  { key: "none", label: "NONE", rule: null },
  { key: "daily", label: "DAILY", freq: "DAILY" },
  { key: "weekdays", label: "WEEKDAYS", freq: "WEEKDAYS" },
  { key: "weekly", label: "WEEKLY", freq: "WEEKLY" },
];

export function presetFromRule(rule) {
  if (!rule) return { key: "none", count: 1 };
  const parts = Object.fromEntries(
    rule
      .split(";")
      .map((c) => c.split("="))
      .filter((kv) => kv.length === 2)
      .map(([k, v]) => [k.toUpperCase(), v.toUpperCase()])
  );
  const freq = parts.FREQ;
  const count = parseInt(parts.COUNT, 10) || 2;
  const found = PRESETS.find((p) => p.freq === freq);
  return { key: found ? found.key : "none", count };
}

export function ruleFromPreset(key, count) {
  const preset = PRESETS.find((p) => p.key === key);
  if (!preset || preset.key === "none") return null;
  const c = Math.max(2, Math.min(30, parseInt(count, 10) || 2));
  return `FREQ=${preset.freq};COUNT=${c}`;
}

export default function RecurrencePicker({ value, onChange, disabled }) {
  const { key, count } = presetFromRule(value);

  const setKey = (next) => {
    if (next === "none") onChange(null);
    else onChange(ruleFromPreset(next, count));
  };

  const setCount = (next) => {
    if (key === "none") return;
    onChange(ruleFromPreset(key, next));
  };

  return (
    <div className="border border-line bg-bg-elevated p-2 space-y-2" data-testid="recurrence-picker">
      <div className="flex items-center gap-2">
        <Repeat className="w-3 h-3 text-ink-mute shrink-0" />
        <span className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute flex-1">REPEAT</span>
        <div className="flex items-center gap-px">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setKey(p.key)}
              disabled={disabled}
              className={clsx(
                "mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors",
                key === p.key
                  ? "border-amber bg-amber text-bg-base"
                  : "border-line text-ink-mute hover:border-amber hover:text-amber"
              )}
              data-testid={`recurrence-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {key !== "none" && (
        <div className="flex items-center gap-2">
          <span className="mono text-2xs uppercase tracking-widest text-ink-mute">OCCURRENCES</span>
          <input
            type="number"
            min={2}
            max={30}
            step={1}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            disabled={disabled}
            className="w-20 mono tabular-nums bg-bg-base text-xs p-1 outline-none border border-line focus:border-amber"
            data-testid="recurrence-count"
          />
          <span className="mono text-2xs text-ink-mute uppercase tracking-widest">incl. first</span>
        </div>
      )}
    </div>
  );
}
